import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import {
  IGood,
  IInventoryBalance,
  IManualAdjust as IManualAdjustment,
  IPurchase,
  ISale,
  ISaleCondition,
  IStockOnDate,
  TSaleSettings,
} from '../entities/goods.interface';

@Injectable({
  providedIn: 'root',
})
export class GoodsService {
  private http = inject(HttpClient);
  private apiUrl = inject(APP_CONFIG).apiUrl;

  public getGoodByDescription(
    description: string
  ): Observable<NonNullable<unknown>> {
    return this.http.get(
      this.apiUrl.odata_goods_service +
        `/Goods?$orderby=Description&$filter=(IsActive eq true) and (contains(tolower(Description),tolower('${description}')))&$top=100`,
      {
        withCredentials: true,
      }
    );
  }

  public getGoodById(id: string): Observable<NonNullable<unknown>> {
    return this.http.get(this.apiUrl.goods + `/Good`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public createGood(good: IGood): Observable<NonNullable<unknown>> {
    return this.http.post(this.apiUrl.goods + `/Good`, good, {
      withCredentials: true,
    });
  }

  public updateGood(good: IGood): Observable<NonNullable<unknown>> {
    return this.http.put(this.apiUrl.goods + `/Good`, good, {
      withCredentials: true,
    });
  }

  public getSaleSettings(id: string): Observable<TSaleSettings> {
    return this.http.get<TSaleSettings>(`${this.apiUrl.goods}/SaleSettings`, {
      params: { id },
      withCredentials: true,
    });
  }

  public updateSaleSettings(
    saleSettings: Partial<TSaleSettings>
  ): Observable<TSaleSettings> {
    return this.http.patch<TSaleSettings>(
      `${this.apiUrl.goods}/SaleSettings`,
      saleSettings,
      {
        withCredentials: true,
      }
    );
  }

  public removeGood(id: string): Observable<NonNullable<unknown>> {
    return this.http.delete(this.apiUrl.goods + `/Good`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public getPurchaseById(id: string): Observable<IPurchase> {
    return this.http.get<IPurchase>(this.apiUrl.goods + `/Purchase`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public createPurchase(purchase: Partial<IPurchase>): Observable<IPurchase> {
    return this.http.post<IPurchase>(
      this.apiUrl.goods + `/Purchase`,
      purchase,
      {
        withCredentials: true,
      }
    );
  }

  public updatePurchase(purchase: Partial<IPurchase>): Observable<IPurchase> {
    return this.http.put<IPurchase>(this.apiUrl.goods + `/Purchase`, purchase, {
      withCredentials: true,
    });
  }

  public removePurchase(id: string): Observable<NonNullable<unknown>> {
    return this.http.delete(this.apiUrl.goods + `/Purchase`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public getSaleById(id: string): Observable<NonNullable<unknown>> {
    return this.http.get(this.apiUrl.goods + `/Sale`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public getSellers(query: {}): Observable<NonNullable<unknown>> {
    return this.http.get(this.apiUrl.odata_person_service + `/Users`, {
      params: query,
      withCredentials: true,
    });
  }

  public getSaleConditions(query: {}): Observable<{
    '@odata.context': string;
    value: ISaleCondition[];
  }> {
    return this.http.get<{ '@odata.context': string; value: ISaleCondition[] }>(
      this.apiUrl.odata_goods_service + `/SaleConditions`,
      {
        params: query,
        withCredentials: true,
      }
    );
  }

  public getGoods(query: {}): Observable<{
    '@odata.context': string;
    value: IGood[];
  }> {
    return this.http.get<{ '@odata.context': string; value: IGood[] }>(
      this.apiUrl.odata_goods_service + `/Goods`,
      {
        params: query,
        withCredentials: true,
      }
    );
  }

  public createSale(sale: Partial<ISale>): Observable<ISale> {
    return this.http.post<ISale>(this.apiUrl.goods + `/Sale`, sale, {
      withCredentials: true,
    });
  }

  public updateSale(sale: Partial<ISale>): Observable<ISale> {
    return this.http.put<ISale>(this.apiUrl.goods + `/Sale`, sale, {
      withCredentials: true,
    });
  }

  public removeSale(id: string): Observable<NonNullable<unknown>> {
    return this.http.delete(this.apiUrl.goods + `/Sale`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public getSaleConditionById(id: string): Observable<NonNullable<unknown>> {
    return this.http.get(this.apiUrl.goods + `/SaleCondition`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public createSaleCondition(
    condition: ISaleCondition
  ): Observable<NonNullable<unknown>> {
    return this.http.post(this.apiUrl.goods + `/SaleCondition`, condition, {
      withCredentials: true,
    });
  }

  public updateSaleCondition(
    condition: ISaleCondition
  ): Observable<NonNullable<unknown>> {
    return this.http.patch(this.apiUrl.goods + `/SaleCondition`, condition, {
      withCredentials: true,
    });
  }

  public removeSaleCondition(id: string): Observable<NonNullable<unknown>> {
    return this.http.delete(this.apiUrl.goods + `/SaleCondition`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public getManualAdjustmentById(id: string): Observable<NonNullable<unknown>> {
    return this.http.get(this.apiUrl.goods + `/StockAdjustment`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public createManualAdjustment(
    adjustment: Partial<IManualAdjustment>
  ): Observable<IManualAdjustment> {
    return this.http.post<IManualAdjustment>(
      this.apiUrl.goods + `/StockAdjustment`,
      adjustment,
      {
        withCredentials: true,
      }
    );
  }

  public updateManualAdjustment(
    adjustment: Partial<IManualAdjustment>
  ): Observable<IManualAdjustment> {
    return this.http.put<IManualAdjustment>(
      this.apiUrl.goods + `/StockAdjustment`,
      adjustment,
      {
        withCredentials: true,
      }
    );
  }

  public adjustStock(
    adjustment: Partial<IManualAdjustment>
  ): Observable<IManualAdjustment> {
    if (adjustment.StockAdjustmentId) {
      return this.updateManualAdjustment(adjustment);
    }

    return this.createManualAdjustment(adjustment);
  }

  public removeManualAdjustment(id: string): Observable<NonNullable<unknown>> {
    return this.http.delete(this.apiUrl.goods + `/StockAdjustment`, {
      params: {
        id,
      },
      withCredentials: true,
    });
  }

  public addRangeStockAdjusment(stockAdjustment: {
    StockAdjustmentViewModel: IInventoryBalance[];
  }): Observable<{ StockAdjustmentViewModel: IInventoryBalance[] }> {
    return this.http.post<{ StockAdjustmentViewModel: IInventoryBalance[] }>(
      this.apiUrl.goods + `/StockAdjustment/AddRangeStockAdjustment`,
      stockAdjustment,
      {
        withCredentials: true,
      }
    );
  }

  public getStockOnDate(goodId: string, on: string): Observable<IStockOnDate> {
    return this.http.get<IStockOnDate>(this.apiUrl.goods + `/GetStockOnDate`, {
      params: {
        goodId,
        on,
      },
      withCredentials: true,
    });
  }
}
